import {
  Button,
  Icon,
  List,
  ListItem,
  PlainButton,
  Popover,
  PopoverContent,
  Truncated,
} from '@customerio/pluma-components/react';
import { FeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import * as styles from './index.css';
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useStore } from '@nanostores/react';
import { currentSessionStore, sessionsStore, updateSessions, changeToSession } from '@customerio/hydra-session-state';
import { useServicesQuery } from '@/services/api';

export function AccountSwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  const is2026LayoutRefresh = useFeatureFlag(FeatureFlag.LayoutRefresh2026);
  const currentSession = useStore(currentSessionStore);
  const allSessions = useStore(sessionsStore);

  const { data, isLoading, error } = useServicesQuery('getSessionMetadata', {});

  useEffect(() => {
    if (isLoading || error != null || data?.sessions == null) {
      return;
    }

    updateSessions(
      data.sessions.map((session) => ({ id: session.index, name: session.account_name, email: session.email })),
    );
  }, [data, isLoading, error]);

  const handleSwitchAccount = (index: number) => {
    if (index === currentSession?.id) {
      setIsOpen(false);
      return;
    }

    changeToSession(index);
    setIsOpen(false);
    // Reload the page to clear loaded data and re-fetch with the new session
    window.location.reload();
  };

  const handleAddAccount = async () => {
    // TODO(multi-account-switcher): implement
    setIsOpen(false);
  };

  if (allSessions.length === 0 || currentSession == null) {
    return null;
  }

  return (
    <Popover isOpen={isOpen} onOpenChange={setIsOpen} placement="bottom-start" withArrow={false}>
      {(context) => (
        <>
          {is2026LayoutRefresh ? (
            <Button
              ref={context.reference.setReference}
              {...context.reference.getReferenceProps()}
              size="sm"
              variant="subtle"
              className={styles.accountSwitcherButton}
              aria-label={`${currentSession.name} account`}
              aria-description="Click to open account selector."
            >
              <Truncated color="base" text="product-pxs-active">
                {currentSession.name}
              </Truncated>
              <Icon name="chevron-toggle" />
            </Button>
          ) : (
            <PlainButton
              ref={context.reference.setReference}
              {...context.reference.getReferenceProps()}
              pl="125"
              className={clsx(styles.accountSwitcherButton, styles.accountSwitcherButtonOldUI)}
              aria-label={`${currentSession.name} account`}
              aria-description="Click to open account selector."
            >
              <Truncated color="invert" text="product-pxs-active">
                {currentSession.name}
              </Truncated>
              <Icon name="chevron-toggle" size="sm" color="subtle" />
            </PlainButton>
          )}
          <PopoverContent className={styles.accountSwitcherContent}>
            <section className={styles.sessionListSection}>
              <List styleType="none">
                {allSessions.map((session) => (
                  <ListItem
                    className={styles.sessionListItem}
                    key={session.email}
                    aria-current={currentSession?.id === session.id}
                  >
                    <PlainButton
                      w="full"
                      px="200"
                      py="150"
                      textAlign="left"
                      onClick={() => handleSwitchAccount(session.id)}
                    >
                      <Truncated display="block" text="product-p-active" aria-label={`${session.name} account`}>
                        {session.name}
                      </Truncated>
                      <Truncated display="block" text="product-pxs" color="subtle">
                        {session.email}
                      </Truncated>
                    </PlainButton>
                  </ListItem>
                ))}
              </List>
            </section>
            <section className={styles.sessionListSection}>
              <PlainButton
                className={styles.sessionListItem}
                px="200"
                py="150"
                display="flex"
                alignItems="center"
                gap="050"
                w="full"
                onClick={handleAddAccount}
              >
                <Icon name="add" />
                Add another account
              </PlainButton>
            </section>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
}
